/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, Typography } from "@mui/material";
import React from "react";

import MaterialSymbol from "../../../components/MaterialSymbol";
import { TextIconButton } from "../../../components/TextIconButton";
import { usePermission } from "../../../hooks/usePermission";
import { useAppDispatch } from "../../../store/rootStore";
import TestIds from "../../../testIds";
import { getDeclarations } from "../declarations.slice";

import DeclarationModal from "./DeclarationsModal";

interface Props {
  children?: React.ReactNode | React.ReactNode[];
}

export const Declarations = (props: Props) => {
  const canViewSalesAgreementsDeclarations = usePermission(
    "SalesAgreements_DeclarationsView"
  ).allowed;
  const [open, setOpen] = React.useState(false);
  const dispatch = useAppDispatch();

  const close = React.useCallback(() => {
    setOpen(false);
  }, []);

  const onClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setOpen(true);
    dispatch(getDeclarations());
    e.stopPropagation();
  }, []);

  if (!canViewSalesAgreementsDeclarations) {
    return null;
  }

  return (
    <Box onClick={onClick} display="inline-flex" flexGrow="1">
      {props.children ?? <DeclarationsButton />}
      <DeclarationModal open={open} handleClose={close} />
    </Box>
  );
};

export const DeclarationsButton = () => {
  return (
    <TextIconButton
      data-testid={TestIds.feat.declarations.openDeclarations}
      color={colors.primary700}
    >
      <MaterialSymbol>folder</MaterialSymbol>
      <Typography variant="body2" color={"inherit"} sx={{ textDecoration: "underline" }}>
        Declarations
      </Typography>
    </TextIconButton>
  );
};

export default Declarations;
